import BasePresenter from '@src/js/separate-logic/base-presenter';
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import ErrorBoundary from '@com/error-boundary';
import { STATUS_BAR_HEIGHT } from '@editor/src/js/constants';
import { isNewCreatedEmptyPpt, checkIsNewCreatedEmptyPpt, slidesStatus } from '@utils/new-empty-ppt';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { getIsNeedRecommend, getTemplateInfos } from './utils';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { tdw } from '@tencent/tencent-doc-report';
import { reaction, IReactionDisposer } from 'mobx';
import { ITextEditorStateService } from '@src/slide/application/service/text-editor-state-service/text-editor-state-service.interface';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { SlideDataServices, onSlideAppReady } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';


const TMallRecommendation = React.lazy(() => import('@tencent/docs-tmall-recommendation'));
const ToolbarContainerId = 'workbench-toolbar';
const SaveTipsId = 'docs-component-save-tips';
const StatusbarClass = 'status-bar';
const TitlebarBtnClass = 'titlebar-btn-container';
const CUSTOM_TEMPLATE = 2;

export const TEMPLATE_RECOMMENDATION_CONTAINER = 'template-recommendation-container';

export default class TemplateRecommendationPesenter extends BasePresenter {
    private hasApply: boolean; // 用于临时埋点和验证
    private textEditReaction: IReactionDisposer | null;
    private cacheOriginTitle: string; // 用于缓存最原始的标题

    public init() {
        this.hasApply = false;
        this.textEditReaction = null;
    }
    public componentDidMount() {
        super.componentDidMount();
        const sdkReadyCb = () => {
            const { isOwner, isDeleted } = SlideDataServices.DocInfoManager() ?? {};
            if (!isOwner || isDeleted) return;
            if (isNewCreatedEmptyPpt()) {
                this.renderTMallRecommendationIfNeed();
                return;
            }

            if (!slidesStatus()) {
                // 如果还未获取数据，则需等待获取到数据
                eventBus.on(EventKeys.UI_MODEL_BIND_SDK, this.renderTMallRecommendationByUIModelChange);
            }
        };
        onSlideAppReady(sdkReadyCb);
        SlideDataServices.SlideTitleManager.promise().then(titleManager => this.cacheOriginTitle = titleManager.title);
        this.addEventListener();
    }

    public hideTemplateRecommendation = () => {
        if (this.model.isShowTemplateRecommendation) {
            this.setModel({
                isShowTemplateRecommendation: false,
            });
            this.dispose();
        }
    };

    private addEventListener = () => {
        /**
         * 以下三种情况关闭推荐模板
         * 1. 收到 apply_changeset 事件，即数据发生变更
         * 2. 点击顶部工具栏、底部statusbar、统一顶部栏里的菜单项
         * 3. 文字编辑(因为文字编辑不会触发changeset, 所以单独处理)
         */
        SlideDataServices.SDKDataServices.EventManager()?.mutationEvents.onCommitCommand(() => {
            if (!this.model.isShowTemplateRecommendation) {
                return;
            }
            this.hideTemplateRecommendation();
        });
        document.addEventListener('click', this.closeIfClickOther, true);
        this.textEditReaction = reaction(
            () => this.getTextEditorStateService().editorContentChangeFlag,
            () => {
                this.hideTemplateRecommendation();
            },
        );
    };

    private renderTMallRecommendationIfNeed = async () => {
        const isNeedRecommend = await getIsNeedRecommend();
        if (isNeedRecommend && this.model.isShowTemplateRecommendation) {
            this.renderTMallRecommendation();
        }
    };

    private renderTMallRecommendation = () => {
        const container = document.getElementById(TEMPLATE_RECOMMENDATION_CONTAINER);

        ReactDOM.render(
            <ErrorBoundary>
                <Suspense fallback={<div></div>}>
                    <TMallRecommendation
                        id={'emptyPptReco'}
                        type='slide'
                        mask={true}
                        maskStyle={{
                            bottom: STATUS_BAR_HEIGHT,
                        }}
                        style={{
                            width: 'calc(100% - 180px)',
                            left: '180px',
                        }}
                        // 开关状态, 调用到 render 说明开关一定为 true, 这里传给组件目的是让组件内部不再发请求
                        switch={true}
                        onClose={this.close}
                        container={container || undefined}
                        onClickTemplate={this.onClickTemplate}
                        fromPage={'ppt'}    // 上报用
                    />
                </Suspense>
            </ErrorBoundary>,
            document.getElementById(TEMPLATE_RECOMMENDATION_CONTAINER),
        );
    };

    private close = () => {
        this.hideTemplateRecommendation();
    };

    private closeIfClickOther = (evt) => {
        const path = evt.path || (evt.composedPath?.()) || [];
        if (!path.find((elem: { id: string, className: string }) => {
            if (elem.id === ToolbarContainerId || elem.className?.indexOf?.(StatusbarClass) > -1) {
                // 点击工具栏或者底部bar的时候关闭
                return true;
            }
            if (elem.id === SaveTipsId || elem.className === TitlebarBtnClass) {
                // 点击统一顶部栏内菜单项
                return true;
            }
            return false;
        })) {
            return;
        }
        this.hideTemplateRecommendation();
    };

    private onClickTemplate = async (template) => {
        const presentationStr = await getTemplateInfos(template.id, template.source === CUSTOM_TEMPLATE);
        this.applyTemplate(presentationStr, template.title);
        this.hideTemplateRecommendation();
    };

    /**
     * 确认应用模板，替换当前的Presentation
     */
    private applyTemplate = async (presentationStr, templateTitle) => {
        const isV3Data = !presentationStr.includes('slideWidth');
        const presentationStoreService = await SlideDataServices.SDKDataServices.PresentationStoreService.promise();
        const importPresentationStoreService = isV3Data
            ? presentationStoreService.createFromSlideFileData(JSON.parse(presentationStr))
            : presentationStoreService.createFromOldPresentationData(presentationStr);
        const presentationModel = this.getUiDataService().presentation;
        presentationModel.replacePresentationData(importPresentationStoreService.getPresentation().getOwnedData());
        this.renameSlideTitle(templateTitle);
        this.saveApplyTemplateStatus();
    };

    private renameSlideTitle = (templateTitle) => {
        const titleManager = SlideDataServices.SlideTitleManager();
        // 如果标题被手动修改过，则不需要应用模版的标题
        if (titleManager?.title !== this.cacheOriginTitle) {
            return;
        }
        titleManager?.updateTitle(templateTitle);
    };

    private saveApplyTemplateStatus = () => {
        tdw({
            opername: 'doc_template_web',
            module: 'detail_page_recommend',
            action: 'recommend_template_apply_slide',
            ver9: 'slide_check_undo',
        });
        this.hasApply = true;
    };

    // private replaceMasterLayoutId = (presentation, templateId) => {
    //     presentation.data.masterList.forEach((master) => {
    //         master.id = `${master.id}-${templateId}`;
    //         master.themeId = `${master.themeId}-${templateId}`;
    //     });
    //     presentation.data.layoutList.forEach((layout) => {
    //         layout.id = `${layout.id}-${templateId}`;
    //         layout.masterId = `${layout.masterId}-${templateId}`;
    //     });
    //     presentation.data.slideList.forEach((slide) => {
    //         slide.layoutId = `${slide.layoutId}-${templateId}`;
    //     });
    //     presentation.data.themeList.forEach((theme) => {
    //         theme.id = `${theme.id}-${templateId}`;
    //     });
    //     return presentation;
    // };

    private dispose = () => {
        document.removeEventListener('click', this.closeIfClickOther);
        this.textEditReaction?.();
        this.textEditReaction = null;
        eventBus.off(EventKeys.UI_MODEL_BIND_SDK, this.renderTMallRecommendationByUIModelChange);
    };

    private renderTMallRecommendationByUIModelChange = ({ slideCount, slides }) => {
        let count = slideCount;
        if (!slideCount && Array.isArray(slides)) {
            count = slides.length;
        }
        if (checkIsNewCreatedEmptyPpt(count, slides)) {
            this.renderTMallRecommendationIfNeed();
        }
    };

    private getUiDataService(): IUiDataService {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices).getUiDataService();
    }
    private getTextEditorStateService(): ITextEditorStateService {
        const appService = AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices;
        return appService.viewableServiceLayer.textEditorStateService;
    }
}
